import { createRoute, redirect } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { UpgradeDialog } from '@/components/panels/UpgradeDialog'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { usePlatform } from '@/hooks/usePlatform'
import { apiClient } from '@/lib/api'
import {
  useAllSamples,
  useUpdateSample,
  useUpdateLocalSample,
  type Sample,
  type SampleSlice,
} from '@/hooks/useSampleUpload'
import { useAudioEngine, type KitId } from '@/hooks/useAudioEngine'
import { WaveformViewer } from '@/components/slicer/WaveformViewer'
import { SliceMarkers } from '@/components/slicer/SliceMarkers'
import { SliceIndexRibbon } from '@/components/slicer/SliceIndexRibbon'
import { TrimMarker } from '@/components/slicer/TrimMarker'
import {
  ChopControls,
  type ChopStrategy,
} from '@/components/slicer/ChopControls'
import { SlicePadGrid } from '@/components/slicer/SlicePadGrid'
import { GRANULAR_PAD_PRESETS } from '@/lib/granular'
import type { BankId } from '@busswerks/shared'
import { SampleUploadButton } from '@/components/sequencer/SampleUploadButton'
import { GranularPreview } from '@/components/slicer/GranularPreview'
import { ScrubPad } from '@/components/slicer/ScrubPad'
import { LoFiPanel } from '@/components/slicer/LoFiPanel'
import BpmDetectorWorker from '@/lib/bpmDetector.worker?worker'
import { findOnsetPeaks, type DetectedBPM } from '@/lib/bpmDetector'
import { smartChop } from '@/lib/chopScorer'
import { generateId } from '@/lib/utils'
import { computeNormalizeGain, linearToDb } from '@/lib/sampleNormalize'
import {
  selectCurrentContainer,
  selectCurrentProject,
  useSequencerStore,
} from '@/stores/sequencerStore'
import { warpOffline, type WarpMode } from '@/lib/rubberband'

/**
 * Find the first non-silent zero crossing in a buffer. Used to seed
 * the trim point on sample load — most uploaded breaks have ~0.05–
 * 0.5s of leading silence/noise, and snapping to a zero crossing
 * (rather than peak energy) avoids the click that otherwise happens
 * when the first slice plays. Returns 0 if the buffer is silent or
 * starts with audible content from t=0.
 *
 *  - Walks the left channel in small windows, computing simple RMS.
 *  - The first window with RMS above `silenceThreshold` is "audio
 *    starts here." Then we scan that window for the first zero
 *    crossing and return its time. If none, return the window's
 *    start time (better than nothing).
 */
function findFirstNonSilentZeroCrossing(
  buffer: AudioBuffer,
  silenceThreshold = 0.01,
  windowSize = 512,
): number {
  const data = buffer.getChannelData(0)
  const sampleRate = buffer.sampleRate
  if (sampleRate <= 0 || data.length === 0) return 0
  for (let start = 0; start + windowSize <= data.length; start += windowSize) {
    let sum = 0
    for (let i = start; i < start + windowSize; i++) {
      const v = data[i]
      sum += v * v
    }
    const rms = Math.sqrt(sum / windowSize)
    if (rms < silenceThreshold) continue
    // Found audible window — scan for first zero crossing inside.
    for (let i = start; i < start + windowSize - 1; i++) {
      const a = data[i]
      const b = data[i + 1]
      if ((a <= 0 && b > 0) || (a >= 0 && b < 0)) {
        // Linear-interpolate the crossing for sub-sample accuracy.
        const frac = -a / (b - a)
        return (i + frac) / sampleRate
      }
    }
    // No zero crossing in the audible window — return its start.
    return start / sampleRate
  }
  // Buffer is silent throughout.
  return 0
}

/**
 * Mirror of findFirstNonSilentZeroCrossing — walks backward from
 * the file end to find the LAST non-silent zero crossing. Used
 * to seed `trimEndSec` so trailing silence/room tone gets cropped
 * automatically on sample load. Returns `buffer.duration` if the
 * buffer is silent (i.e. don't crop anything).
 */
function findLastNonSilentZeroCrossing(
  buffer: AudioBuffer,
  silenceThreshold = 0.01,
  windowSize = 512,
): number {
  const data = buffer.getChannelData(0)
  const sampleRate = buffer.sampleRate
  if (sampleRate <= 0 || data.length === 0) return 0
  for (let start = data.length - windowSize; start >= 0; start -= windowSize) {
    let sum = 0
    for (let i = start; i < start + windowSize; i++) {
      const v = data[i]
      sum += v * v
    }
    const rms = Math.sqrt(sum / windowSize)
    if (rms < silenceThreshold) continue
    // Found audible window — scan backward for the last zero
    // crossing inside it.
    for (let i = start + windowSize - 2; i >= start; i--) {
      const a = data[i]
      const b = data[i + 1]
      if ((a <= 0 && b > 0) || (a >= 0 && b < 0)) {
        const frac = -a / (b - a)
        return (i + frac) / sampleRate
      }
    }
    // No zero crossing — return the window's end.
    return (start + windowSize) / sampleRate
  }
  // Silent — don't crop.
  return buffer.duration
}

export const werkshopSamplerToolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/tools/sampler',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: SlicerPage,
})

/**
 * Sample Slicer workspace. Free-tier users are shunted to the
 * upgrade dialog on entry; paid users get the full editor. The
 * three interior panels handle (1) waveform + beat-grid rendering,
 * (2) draggable slice-boundary overlay, (3) BPM/bars/snap toolbar.
 *
 * State lives at this component level — not in a Zustand store —
 * because it is purely per-session editing scratch. Hitting Save
 * PATCHes the persisted fields (`bpm`, `bars`, `bpmConfidence`,
 * `slices`) onto the sample's DynamoDB row, which then flows back
 * into the sequencer via the shared `useSamples` query cache.
 */
function SlicerPage() {
  const { features } = useEffectiveFeatures()
  const allowed = features?.sampleSlicer ?? false
  const [upgradeOpen, setUpgradeOpen] = useState(!allowed)
  const platform = usePlatform()

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader title="Sampler" workspaceSwitcher="werkshop" />
      <main
        className={
          // Cap the Slicer at `max-w-6xl` on every platform. The
          // old `w-full` native variant let the tools column
          // (granular preview + scrub pad) sprawl across the full
          // iPad-landscape / Catalyst viewport, which dwarfed the
          // slice pads and pushed the sliders past the screen edge
          // on wide monitors. A fixed cap keeps the control
          // density roughly constant regardless of screen size.
          platform.isNative
            ? 'max-w-6xl mx-auto w-full px-3 sm:px-4 py-4 flex flex-col gap-4'
            : 'max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6'
        }
      >
        {allowed ? (
          <SlicerEditor />
        ) : (
          <section className="rounded-xl border border-brd-subtle bg-surface-raised shadow-low p-6 sm:p-10 text-center">
            <div aria-hidden className="text-5xl sm:text-6xl leading-none mb-3">
              ✂️
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-content-primary mb-1">
              Slicer — upgrade required
            </h2>
            <p className="text-sm text-content-secondary max-w-xl mx-auto leading-relaxed">
              Chop breaks into rhythmically-aligned slices and assign them to
              pads. Pick any paid tier to unlock the full editor.
            </p>
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              className="btn-primary btn-sm mt-6"
            >
              Upgrade to unlock
            </button>
          </section>
        )}
      </main>
      <UpgradeDialog
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
    </div>
  )
}

/**
 * Full editor. Rendered only when the tier gate passes so we
 * don't eagerly spin up an AudioContext or fetch sample bytes
 * for users who can't use the feature.
 */
/** Props for the embeddable Slicer. Today the tool route renders it
 *  with no props so it follows its own per-project active sample;
 *  the Sequencer's Plugin tab passes `sampleIdOverride` so the
 *  slicer always targets the currently-focused pad's sample instead
 *  of the legacy "whatever was last opened from this route" picker. */
export interface SlicerEditorProps {
  /** When set, the slicer's internal `selectedSampleId` is force-
   *  synced to this value whenever it changes. Leave undefined to
   *  keep the legacy free-picker behaviour the standalone route
   *  uses. */
  sampleIdOverride?: string | null
  /** When true, hide the sample picker strip at the top. Used in
   *  the Plugin tab where the pad focus IS the picker — a second
   *  list inside the tab would be redundant and take vertical
   *  space away from the waveform + slice grid. */
  hideSamplePicker?: boolean
}

export function SlicerEditor({
  sampleIdOverride,
  hideSamplePicker = false,
}: SlicerEditorProps = {}) {
  const { data: allSamples = [] } = useAllSamples()
  const updateSample = useUpdateSample()
  const updateLocalSample = useUpdateLocalSample()
  const { trigger, currentTime, resume } = useAudioEngine()
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const setPadContentInBank = useSequencerStore(s => s.setPadContentInBank)
  // Surface which bank gets the slices via "Send to pads" so the
  // user sees where the assignment will land before clicking.
  const activeBankId = useSequencerStore(s => {
    const c = s.containers[s.currentContainerId]
    return c?.activeBankId ?? null
  })

  // Per-project slicer filter: if the active project has registered
  // sample ids, default to showing only those so the slicer scopes
  // to the song being worked on. "All samples" toggle escapes the
  // filter when the user wants to browse their full library.
  const container = useSequencerStore(selectCurrentContainer)
  const setActiveProjectSample = useSequencerStore(
    s => s.setActiveProjectSample,
  )
  const projectSampleIds = container?.slicerConfig.sampleIds ?? []
  const hasProjectScope = projectSampleIds.length > 0
  const [filter, setFilter] = useState<'project' | 'all'>(() =>
    hasProjectScope ? 'project' : 'all',
  )
  // When the user opens the slicer from a project that has samples,
  // keep the toggle in the "project" mode on subsequent visits so the
  // filter feels persistent. Switching projects mid-session re-reads
  // hasProjectScope but doesn't override an explicit "all" selection.
  useEffect(() => {
    if (hasProjectScope) return
    setFilter('all')
  }, [hasProjectScope])

  const samples = useMemo<Sample[]>(() => {
    if (filter === 'all' || !hasProjectScope) return allSamples
    const allowed = new Set(projectSampleIds)
    const filtered = allSamples.filter(s => allowed.has(s.id))
    // If the project references sample ids that haven't loaded yet,
    // we don't want to show an empty list — fall back to all samples
    // until the project's ids resolve.
    return filtered.length > 0 ? filtered : allSamples
  }, [allSamples, filter, hasProjectScope, projectSampleIds])

  const [selectedSampleId, setSelectedSampleId] = useState<string | undefined>(
    () => container?.slicerConfig.activeSampleId,
  )
  const selectedSample = useMemo<Sample | undefined>(
    () => samples.find(s => s.id === selectedSampleId),
    [samples, selectedSampleId],
  )

  // Default to the project's last-active sample, or the first sample
  // once the list loads. Kept as an effect (not a derived default)
  // so the user's explicit picks survive list refetches.
  useEffect(() => {
    if (selectedSampleId) return
    const first =
      container?.slicerConfig.activeSampleId ?? samples[0]?.id ?? undefined
    if (first) setSelectedSampleId(first)
  }, [samples, selectedSampleId, container])

  // Plugin-tab override: when the caller pins a specific sample id
  // (via focused-pad resolution in `PluginPane`), force the internal
  // selection to follow. Runs every time `sampleIdOverride` changes
  // so switching pads in the Sequencer while the Plugin tab is open
  // re-targets the slicer. Undefined override → no-op, the standalone
  // route keeps its own picker behaviour.
  useEffect(() => {
    if (!sampleIdOverride) return
    if (sampleIdOverride === selectedSampleId) return
    setSelectedSampleId(sampleIdOverride)
  }, [sampleIdOverride, selectedSampleId])

  // Echo the user's sample pick back into the project so opening the
  // slicer later resumes where they left off.
  useEffect(() => {
    if (!container || !selectedSampleId) return
    if (container.slicerConfig.activeSampleId === selectedSampleId) return
    setActiveProjectSample(selectedSampleId)
  }, [container, selectedSampleId, setActiveProjectSample])

  const [buffer, setBuffer] = useState<AudioBuffer | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)

  const [bpm, setBpm] = useState<number | undefined>(undefined)
  const [bars, setBars] = useState<number | undefined>(undefined)
  const [bpmConfidence, setBpmConfidence] = useState<number | undefined>(
    undefined,
  )
  // Per-sample gain in dBFS. Multiplied with velocity + per-slice gain
  // at playback (see `sampleNormalize.ts` + `useSequencerTriggers`).
  // `undefined` === 0 dB. Normalize writes a computed value here + a
  // `normalizedPeak` reference so the UI can show "normalized" state.
  const [sampleGainDb, setSampleGainDb] = useState<number | undefined>(
    undefined,
  )
  const [normalizedPeak, setNormalizedPeak] = useState<number | undefined>(
    undefined,
  )
  const [snap, setSnap] = useState<boolean>(true)
  const [sliceCount, setSliceCount] = useState<number>(16)
  const [currentSlices, setCurrentSlices] = useState<SampleSlice[]>([])
  const [isDetecting, setIsDetecting] = useState(false)
  // Phase 1 — trim + peak-aware chopping state.
  const [trimStartSec, setTrimStartSec] = useState<number>(0)
  const [trimEndSec, setTrimEndSec] = useState<number>(0)
  const [autoStartSec, setAutoStartSec] = useState<number | undefined>(
    undefined,
  )
  const [chopStrategy, setChopStrategy] = useState<ChopStrategy>('equal')
  // Zoom state — 1.0 fits the whole sample to the container width.
  // Higher values stretch the canvas + markers and let the user
  // scroll the parent section horizontally for precision editing.
  const [zoomLevel, setZoomLevel] = useState<number>(1)
  /** Detected onset peaks in seconds. Populated by handleAutoDetect
   *  from the bpm worker's onsetEnvelope. Used by chopToStrategy
   *  when strategy === 'transients' | 'smart'. */
  const [onsetPeaks, setOnsetPeaks] = useState<number[]>([])
  /** Full onset envelope + frame rate from the detector, kept so
   *  the Smart-Chop scorer can look up each peak's strength value
   *  without re-running detection. */
  const [onsetEnvelope, setOnsetEnvelope] = useState<Float32Array | null>(null)
  const [onsetFrameRate, setOnsetFrameRate] = useState<number | null>(null)

  // ── Phase 1a: Rubber Band warp state ────────────────────
  //
  // We keep a reference to the ORIGINAL decoded buffer so "Reset to
  // original" is a one-tap undo — warping is offline and produces a
  // brand-new AudioBuffer that replaces `buffer`, so without the
  // backup we couldn't recover the source.
  const originalBufferRef = useRef<AudioBuffer | null>(null)
  const [warpMode, setWarpMode] = useState<WarpMode>('beats')
  const [warpedTo, setWarpedTo] = useState<number | null>(null)
  const [warping, setWarping] = useState(false)

  // Read the active project's BPM so the Warp button knows where to
  // land. Falls back to the sample's detected BPM when no project is
  // in focus (the Slicer is reachable outside a project session).
  const currentProject = useSequencerStore(selectCurrentProject)
  const projectBpm = currentProject?.bpm

  const durationSec = buffer?.duration ?? selectedSample?.durationSec

  // Snap resolution — 16th notes when BPM is set. Passed into
  // SliceMarkers so marker drags quantise to the grid; also used
  // when generating chop-N boundaries so "16 slices on a 1-bar
  // loop" lands on the quarter-note grid.
  const snapSec = useMemo(() => {
    if (!snap || !bpm || bpm <= 0) return undefined
    return 60 / bpm / 4
  }, [snap, bpm])

  // Width of the waveform area — recomputed on mount and resize
  // so the canvas + marker overlay stay flush with the container
  // without baking in a fixed pixel width that fights flex layout.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(960)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const w = el.clientWidth
      if (w > 0) setWidth(w)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  // 180px waveform leaves room below for the scrub pad + slice
  // pads inside one viewport on iPad-landscape without sacrificing
  // transient readability. Canvas scales cleanly to whatever value
  // lands here; 140 and 220 both read fine but 180 is the sweet
  // spot for the current window budget.
  const height = 180

  // Load audio whenever the selected sample changes. Also seeds
  // bpm/bars/bpmConfidence/slices state from the persisted sample
  // fields — clicking around between samples restores each one's
  // last-saved state instead of leaking the previous sample's
  // settings into the next edit session.
  useEffect(() => {
    if (!selectedSample?.playUrl) {
      setBuffer(null)
      setLoadError(undefined)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(undefined)
    const run = async () => {
      try {
        const AnyAC: typeof AudioContext =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        // Dedicated short-lived context for decode — the engine's
        // playback context handles actual audio routing. Keeping
        // the two separate avoids a suspended-context deadlock if
        // the user lands here before their first gesture.
        const ctx = new AnyAC()
        const res = await fetch(selectedSample.playUrl)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const bytes = await res.arrayBuffer()
        const decoded = await ctx.decodeAudioData(bytes)
        originalBufferRef.current = decoded
        setWarpedTo(null)
        if (cancelled) return
        setBuffer(decoded)
        setBpm(selectedSample.bpm)
        setBars(selectedSample.bars)
        setBpmConfidence(selectedSample.bpmConfidence)
        setSampleGainDb(selectedSample.gainDb)
        setNormalizedPeak(selectedSample.normalizedPeak)
        const persisted = selectedSample.slices ?? []
        setCurrentSlices(persisted)
        if (persisted.length > 0) {
          setSliceCount(persisted.length)
          // Persisted slices came from the user's last save session
          // — treat as "user-edited" so the post-onset re-chop
          // doesn't clobber them.
          userEditedSlicesRef.current = true
        } else {
          userEditedSlicesRef.current = false
        }
        // Phase 1: detect first non-silent zero crossing as an
        // auto-trim suggestion. If the persisted sample already has
        // an explicit trim, restore that; otherwise seed with the
        // detected value so newly-loaded breaks crop their leading
        // silence by default.
        const detectedStart = findFirstNonSilentZeroCrossing(decoded)
        setAutoStartSec(detectedStart > 0.001 ? detectedStart : undefined)
        setTrimStartSec(
          typeof selectedSample.trimStartSec === 'number'
            ? selectedSample.trimStartSec
            : detectedStart,
        )
        // Mirror for the trail: detect last non-silent zero crossing
        // and use that as the trim end. If the persisted value
        // exists (and is sane), restore it instead.
        const detectedEnd = findLastNonSilentZeroCrossing(decoded)
        const persistedEnd = selectedSample.trimEndSec
        setTrimEndSec(
          typeof persistedEnd === 'number' && persistedEnd > 0
            ? Math.min(persistedEnd, decoded.duration)
            : detectedEnd,
        )
        // Reset onset-peak cache — they're per-sample and recomputed
        // on the next Auto-detect run.
        setOnsetPeaks([])
        // Reset zoom on every sample change so the user always lands
        // on a fit-to-window view.
        setZoomLevel(1)
        // If the persisted sample carries no slices yet, leave
        // chopStrategy as the user's last choice; the dropdown will
        // use it on the next chop-N click.
      } catch (err) {
        if (!cancelled) {
          setBuffer(null)
          setLoadError(err instanceof Error ? err.message : 'Load failed')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [selectedSample])

  // Interior boundaries derived from current slices — N-1 floats
  // matching the contract SliceMarkers expects.
  const boundaries = useMemo(
    () => currentSlices.slice(0, -1).map(s => s.endSec),
    [currentSlices],
  )

  /**
   * Generate N slices starting from the trim point and ending at
   * the file end. Strategy controls the boundary placement:
   *
   *   'equal' — divide the post-trim window into exactly N equal
   *             slices. No grid snap, no transient anchoring — the
   *             trim points already tell us where the loop lives,
   *             so "equal parts" means arithmetically equal. Snap
   *             is available for transient / smart modes where
   *             interior boundaries are genuinely negotiable.
   *
   *   'transients' — pick the first N detected peaks past trim as
   *              slice starts. Boundaries land exactly on peaks
   *              (peaks ARE the alignment). If we have fewer than
   *              N peaks, fall back to equal-dividing the remaining
   *              window so the user still gets exactly N slices.
   *
   * Slice IDs are preserved by index for back-compat with pads
   * already referencing them (a pad pinned to slice #5 keeps
   * pointing at slot #5 even if the boundaries shift).
   */
  const chopToStrategy = useCallback(
    (count: number, strategy: ChopStrategy) => {
      if (!durationSec || durationSec <= 0 || count < 1) return
      const start = Math.max(0, Math.min(durationSec, trimStartSec))
      // The chop window stops at trimEndSec when set (and within
      // bounds), otherwise at the file's duration.
      const end =
        trimEndSec > start && trimEndSec <= durationSec
          ? trimEndSec
          : durationSec
      const span = end - start
      if (span <= 0) return

      let starts: number[]
      if (
        strategy === 'smart' &&
        onsetPeaks.length > 0 &&
        onsetEnvelope &&
        onsetFrameRate
      ) {
        // Smart-chop — score each onset peak by transient strength,
        // silence gap to neighbours, and bar alignment, then pick
        // the top N. Always includes the trim start as a hard
        // anchor so slice 1 starts where the user expects.
        const usable = onsetPeaks.filter(
          p => p >= start - 0.001 && p <= end - 0.001,
        )
        const picks = smartChop(
          usable,
          onsetEnvelope,
          onsetFrameRate,
          Math.max(1, count - 1), // leave room for the trim anchor
          {
            durationSec: end - start,
            sourceBpm: bpm,
            subdivision: 16,
          },
        )
        const pickTimes = picks.map(p => p.timeSec)
        // Always include the trim start as slice 1 so the tail of
        // any leading silence doesn't become an orphan chop.
        const withAnchor = [start, ...pickTimes.filter(t => t > start + 0.02)]
        if (withAnchor.length >= count) {
          starts = withAnchor.slice(0, count)
        } else {
          // Fill the tail with equal divisions past the last pick.
          const last = withAnchor[withAnchor.length - 1]
          const remaining = count - withAnchor.length
          const tailSpan = end - last
          const tailStep = tailSpan / (remaining + 1)
          const tail: number[] = []
          for (let i = 1; i <= remaining; i++) tail.push(last + tailStep * i)
          starts = [...withAnchor, ...tail]
        }
      } else if (strategy === 'transients' && onsetPeaks.length > 0) {
        const usable = onsetPeaks.filter(
          p => p >= start - 0.001 && p <= end - 0.001,
        )
        if (usable.length >= count) {
          starts = usable.slice(0, count)
        } else {
          // Use the peaks we have, then equal-divide the rest of
          // the window past the last peak.
          const lastPeak = usable[usable.length - 1] ?? start
          const remaining = count - usable.length
          const tailSpan = end - lastPeak
          const tailStep = tailSpan / (remaining + 1)
          const tail: number[] = []
          for (let i = 1; i <= remaining; i++) {
            tail.push(lastPeak + tailStep * i)
          }
          starts = [...usable, ...tail]
        }
      } else {
        // Equal mode (or transients with no peaks yet) — divide the
        // post-trim span into exactly `count` equal slices. No grid
        // snapping, no transient anchoring: when the user asks for
        // "equal parts" they mean mathematically equal parts of the
        // trimmed sample. The grid / snap is a useful tool for
        // transient and smart chop modes where interior boundaries
        // are negotiable, but for equal mode the user's intent is
        // "I've told you where the loop starts and ends — divide it
        // by N." Anything else is us second-guessing the user.
        const step = span / count
        starts = []
        for (let i = 0; i < count; i++) starts.push(start + i * step)
      }

      const out: SampleSlice[] = starts.map((startSec, i) => {
        const next = i + 1 < starts.length ? starts[i + 1] : end
        return {
          id: currentSlices[i]?.id ?? generateId('slice'),
          startSec,
          endSec: next,
          label: currentSlices[i]?.label,
        }
      })
      setCurrentSlices(out)
      setSliceCount(count)
    },
    [
      durationSec,
      trimStartSec,
      trimEndSec,
      onsetPeaks,
      onsetEnvelope,
      onsetFrameRate,
      currentSlices,
      bpm,
    ],
  )

  // Auto-chop on sample load. Fires up to twice per buffer:
  //   1. On first arrival — draw initial chops against whatever
  //      anchor data we have (trimStart is the fallback). Users
  //      see slices immediately instead of a blank pad grid.
  //   2. Again once the onset detector delivers peaks — the grid
  //      anchor now falls on an actual transient, so the slices
  //      re-snap to the real drum grid without a user gesture.
  //      Gated on `!userEditedSlicesRef` so any manual tweak after
  //      initial auto-chop sticks.
  const autoChoppedForBufferRef = useRef<AudioBuffer | null>(null)
  const reChoppedWithPeaksRef = useRef<AudioBuffer | null>(null)
  const userEditedSlicesRef = useRef(false)
  useEffect(() => {
    if (!buffer) return
    if (!durationSec || durationSec <= 0) return
    const hasInitial = autoChoppedForBufferRef.current === buffer
    const hasReChop = reChoppedWithPeaksRef.current === buffer
    if (!hasInitial) {
      if (currentSlices.length > 0) return
      autoChoppedForBufferRef.current = buffer
      userEditedSlicesRef.current = false
      chopToStrategy(sliceCount, chopStrategy)
      return
    }
    if (!hasReChop && onsetPeaks.length > 0 && !userEditedSlicesRef.current) {
      reChoppedWithPeaksRef.current = buffer
      chopToStrategy(sliceCount, chopStrategy)
    }
  }, [
    buffer,
    currentSlices.length,
    durationSec,
    sliceCount,
    chopStrategy,
    chopToStrategy,
    onsetPeaks.length,
  ])

  // Auto-run the onset detector on sample load so the grid anchor
  // always has real transient data rather than falling back to
  // `trimStart` (which lands on the first non-silent zero crossing
  // — close to the first hit, but not exactly on it). One-shot
  // per buffer via its own ref.
  const autoDetectedForBufferRef = useRef<AudioBuffer | null>(null)

  const handleBoundariesChange = useCallback(
    (next: number[]) => {
      if (!durationSec) return
      // User dragged a slice boundary — lock auto re-chop.
      userEditedSlicesRef.current = true
      const points = [0, ...next, durationSec]
      setCurrentSlices(prev =>
        points.slice(0, -1).map((startSec, i) => ({
          id: prev[i]?.id ?? generateId('slice'),
          startSec,
          endSec: points[i + 1],
          label: prev[i]?.label,
        })),
      )
    },
    [durationSec],
  )

  // Auto-detect via Web Worker. Kept in a ref so we can terminate
  // a previous run if the user clicks Auto-detect again mid-pass.
  const workerRef = useRef<Worker | null>(null)
  useEffect(() => {
    return () => {
      workerRef.current?.terminate()
      workerRef.current = null
    }
  }, [])

  // ── Rubber Band offline warp ──
  //
  // Runs on the user's explicit tap rather than on every BPM tweak
  // so the user controls when the (potentially slow) WASM pass
  // fires. We always warp from the ORIGINAL buffer so successive
  // target changes don't accumulate quality loss — each warp is a
  // fresh rendering from the pristine source.
  const handleWarp = useCallback(async () => {
    const src = originalBufferRef.current
    if (!src || warping) return
    if (!bpm || bpm <= 0 || !projectBpm || projectBpm <= 0) return
    if (!selectedSampleId) return
    setWarping(true)
    try {
      const warped = await warpOffline(
        src,
        { sourceBpm: bpm, targetBpm: projectBpm, mode: warpMode },
        selectedSampleId,
      )
      setBuffer(warped)
      setWarpedTo(projectBpm)
      // The chops stored in seconds no longer line up — the warped
      // buffer has a different duration. Clear them so the user
      // re-chops against the new transient positions.
      setCurrentSlices([])
      setOnsetPeaks([])
      // Update the working BPM so subsequent chop math uses the new
      // tempo. `bpm` is "what this buffer now plays at".
      setBpm(projectBpm)
    } catch (err) {
      console.error('Rubber Band warp failed:', err)
    } finally {
      setWarping(false)
    }
  }, [bpm, projectBpm, warpMode, selectedSampleId, warping])

  const handleResetWarp = useCallback(() => {
    const src = originalBufferRef.current
    if (!src) return
    setBuffer(src)
    setWarpedTo(null)
    setCurrentSlices([])
    setOnsetPeaks([])
    // Restore the source BPM so chop math matches the original again.
    if (selectedSample?.bpm) setBpm(selectedSample.bpm)
  }, [selectedSample])

  const handleAutoDetect = useCallback(() => {
    if (!buffer || isDetecting) return
    workerRef.current?.terminate()
    const worker = new BpmDetectorWorker()
    workerRef.current = worker
    setIsDetecting(true)
    // Copy channel 0 into a transferable Float32Array. The worker
    // does not need sub-sample accuracy; left channel is a fine
    // proxy for drum-energy onset structure.
    const src = buffer.getChannelData(0)
    const samples = new Float32Array(src.length)
    samples.set(src)
    worker.onmessage = (e: MessageEvent<DetectedBPM>) => {
      setBpm(e.data.bpm)
      setBpmConfidence(e.data.confidence)
      // Extract transient peaks from the onset envelope returned
      // alongside the BPM. Min spacing of ~50ms suppresses ringing
      // tails that would otherwise surface as duplicate peaks.
      const minSpacingFrames = Math.max(1, Math.round(0.05 * e.data.frameRate))
      const peakFrames = findOnsetPeaks(
        e.data.onsetEnvelope,
        0.3,
        minSpacingFrames,
      )
      const peakSeconds = peakFrames.map(f => f / e.data.frameRate)
      setOnsetPeaks(peakSeconds)
      setOnsetEnvelope(e.data.onsetEnvelope)
      setOnsetFrameRate(e.data.frameRate)
      // If a peak landed earlier than the current trim suggestion,
      // refine autoStartSec to that — the autodetector is more
      // confident than the simple RMS-zero-crossing pass.
      if (peakSeconds.length > 0) {
        setAutoStartSec(prev => {
          if (prev === undefined) return peakSeconds[0]
          return Math.min(prev, peakSeconds[0])
        })
      }
      setIsDetecting(false)
      worker.terminate()
      if (workerRef.current === worker) workerRef.current = null
    }
    worker.onerror = () => {
      setIsDetecting(false)
      worker.terminate()
      if (workerRef.current === worker) workerRef.current = null
    }
    worker.postMessage({ samples, sampleRate: buffer.sampleRate }, [
      samples.buffer,
    ])
  }, [buffer, isDetecting])

  // Fire the detector automatically on every sample load. Without
  // this the grid anchor for auto-chop falls back to `trimStart`
  // (which is only approximately on the first hit) so the initial
  // slices can land slightly off the real drum grid. Once the
  // detector completes, the auto-chop effect re-runs with the real
  // peaks and re-snaps the slices. Gated by a ref so we don't
  // retrigger mid-detection or on unrelated re-renders.
  useEffect(() => {
    if (!buffer || isDetecting) return
    if (autoDetectedForBufferRef.current === buffer) return
    autoDetectedForBufferRef.current = buffer
    handleAutoDetect()
  }, [buffer, isDetecting, handleAutoDetect])

  const handlePlay = useCallback(
    (sliceIndex?: number) => {
      if (!selectedSample?.playUrl) return
      void resume()
      const slice =
        sliceIndex !== undefined ? currentSlices[sliceIndex] : undefined
      trigger('classic' as KitId, null, currentTime(), {
        velocity: 0.95,
        sample: {
          sampleId: selectedSample.id,
          playUrl: selectedSample.playUrl,
          sliceStart: slice?.startSec,
          sliceLenSec: slice
            ? Math.max(0, slice.endSec - slice.startSec)
            : undefined,
        },
      })
    },
    [selectedSample, currentSlices, trigger, currentTime, resume],
  )

  /**
   * Populate the current bank's pad grid with the chopped slices,
   * one slice per pad starting at pad 0. Caps at 16 pads (the
   * grid max) — extra slices are ignored. Each pad gets a
   * chromatic midiNote (C3 + padIndex) so hardware mapping
   * stays predictable.
   *
   * Writes through `setPadContent`, which mirrors into both the
   * active pattern's padDefs (for live playback) and the owning
   * bank's padDefs (what sync persists).
   */
  const [sentToPads, setSentToPads] = useState(false)
  const handleSendToPads = useCallback(() => {
    if (!selectedSample || currentSlices.length === 0) return
    const slicesToSend = currentSlices.slice(0, 16)
    const sampleName = selectedSample.name ?? 'Sample'
    slicesToSend.forEach((slice, i) => {
      setPadContent(i, {
        kind: 'sample',
        sampleId: selectedSample.id,
        s3Key: selectedSample.s3Key ?? '',
        sliceId: slice.id,
        // Pad label prefers the slice's user label, falling back
        // to "SampleName · N" so every pad reads distinctly.
        name: slice.label ?? `${sampleName} · ${i + 1}`,
        midiNote: 48 + i,
      })
    })
    setSentToPads(true)
    // 2 s confirmation window — long enough to read, short enough
    // not to stick around into the next edit gesture.
    window.setTimeout(() => setSentToPads(false), 2000)
  }, [selectedSample, currentSlices, setPadContent])

  /**
   * Route a single slice to a user-chosen (bank, pad) without
   * touching anything else. Resolves to the same sample-kind
   * `PadContent` shape that the bulk "Send to Bank" button uses,
   * then writes through `setPadContentInBank` so the assignment
   * lands on the requested bank regardless of which is currently
   * active.
   */
  const handleAssignSliceToPad = useCallback(
    (
      sliceIndex: number,
      bankId: BankId,
      padIndex: number,
      kind: 'sample' | 'granular' | 'instrument' = 'sample',
    ) => {
      if (!selectedSample) return
      const slice = currentSlices[sliceIndex]
      if (!slice) return
      const sampleName = selectedSample.name ?? 'Sample'
      const label = slice.label ?? `${sampleName} · ${sliceIndex + 1}`
      if (kind === 'instrument') {
        // Sampler-instrument branch — routes the slice through the
        // Wave 3 instrument host (buffer injected at fire time).
        // Writes `sliceStart` + `sliceLen` explicitly so the
        // trigger path doesn't need to re-derive from the sample's
        // slice list (users can freely rebase slices on the sample
        // without breaking pads).
        setPadContentInBank(bankId, padIndex, {
          kind: 'instrument',
          instrumentId: 'sampler',
          midiNote: 48 + padIndex,
          label,
          preset: {},
          gainDb: 0,
          sampleId: selectedSample.id,
          s3Key: selectedSample.s3Key ?? '',
          sliceId: slice.id,
          sliceStart: slice.startSec,
          sliceLen: slice.endSec - slice.startSec,
        })
        return
      }
      if (kind === 'granular') {
        // Granular slices seed the Freeze preset and pin
        // positionSec at the slice's start so the first hit drops
        // the playhead exactly on the chop boundary. Users can
        // swap preset / scrub position later in the PadEditor.
        const seed = GRANULAR_PAD_PRESETS.freeze
        setPadContentInBank(bankId, padIndex, {
          kind: 'granular',
          sampleId: selectedSample.id,
          s3Key: selectedSample.s3Key ?? '',
          name: label,
          midiNote: 48 + padIndex,
          preset: 'freeze',
          grainSizeMs: seed.grainSizeMs,
          overlap: seed.overlap,
          detuneCents: seed.detuneCents,
          playbackRate: seed.playbackRate,
          reverse: seed.reverse,
          positionSec: slice.startSec,
          positionSpread: seed.positionSpread,
          pitchSpread: seed.pitchSpread,
          loop: seed.loop,
        })
        return
      }
      setPadContentInBank(bankId, padIndex, {
        kind: 'sample',
        sampleId: selectedSample.id,
        s3Key: selectedSample.s3Key ?? '',
        sliceId: slice.id,
        name: label,
        midiNote: 48 + padIndex,
      })
    },
    [selectedSample, currentSlices, setPadContentInBank],
  )

  const handleSave = useCallback(async () => {
    if (!selectedSample) return
    const updates = {
      bpm: bpm ?? null,
      bars: bars ?? null,
      bpmConfidence: bpmConfidence ?? null,
      slices: currentSlices.length > 0 ? currentSlices : null,
      // Persist trim only if it's actually been moved past 0.
      // Sending null clears it on the server side so a sample
      // returns to "no trim" when the user drags it back.
      trimStartSec: trimStartSec > 0.001 ? trimStartSec : null,
      // Persist trim end only when it actually crops (less than the
      // full duration). null clears it so a sample returns to
      // full-length playback if the user drags it to the end.
      trimEndSec:
        durationSec && trimEndSec > 0 && trimEndSec < durationSec - 0.001
          ? trimEndSec
          : null,
      // Gain controls (F1 follow-up). `0` persists as null so the
      // DB row stays clean when the user resets gain; any non-zero
      // value or an explicit normalize write persists through.
      gainDb:
        sampleGainDb !== undefined && sampleGainDb !== 0 ? sampleGainDb : null,
      normalizedPeak:
        normalizedPeak !== undefined && normalizedPeak > 0
          ? normalizedPeak
          : null,
    }
    if (selectedSample.source === 'local') {
      await updateLocalSample.mutateAsync({ id: selectedSample.id, updates })
    } else {
      await updateSample.mutateAsync({ id: selectedSample.id, updates })
    }
  }, [
    selectedSample,
    updateSample,
    updateLocalSample,
    bpm,
    bars,
    bpmConfidence,
    currentSlices,
    trimStartSec,
    trimEndSec,
    durationSec,
    sampleGainDb,
    normalizedPeak,
  ])

  const handleTrimToFirstTransient = useCallback(() => {
    if (autoStartSec !== undefined && autoStartSec > 0) {
      setTrimStartSec(autoStartSec)
    }
  }, [autoStartSec])

  const handleStrategyChange = useCallback(
    (strategy: ChopStrategy) => {
      setChopStrategy(strategy)
      // If the user already has slices, re-chop to the new
      // strategy so the change is immediately visible. Otherwise
      // just store the preference for the next chop-N click.
      if (currentSlices.length > 0) {
        chopToStrategy(currentSlices.length, strategy)
      }
    },
    [chopToStrategy, currentSlices.length],
  )

  /**
   * Delete slice at index. Range absorbs into the neighbour so the
   * total time covered by slices stays continuous (no gaps), which
   * keeps "Chop into N" math sane and the visual marker layout
   * gap-free. Pads referencing the deleted slice's id fall back to
   * full-sample playback per the engine's existing missing-sliceId
   * fallback.
   *
   * Rules:
   *   - delete first slice: next slice's startSec extends back to
   *     trim point (or 0)
   *   - delete any other slice: previous slice's endSec extends
   *     forward to deleted slice's endSec
   *   - delete only remaining slice: clear everything
   */
  const handleDeleteSlice = useCallback((index: number) => {
    // User deleted a slice — lock auto re-chop.
    userEditedSlicesRef.current = true
    setCurrentSlices(prev => {
      if (index < 0 || index >= prev.length) return prev
      if (prev.length === 1) {
        setSliceCount(0)
        return []
      }
      const next = prev.slice()
      const removed = next.splice(index, 1)[0]
      if (index === 0) {
        // Extend the new first slice back to the trim point.
        next[0] = { ...next[0], startSec: removed.startSec }
      } else {
        // Extend the previous slice forward.
        const prevSlice = next[index - 1]
        next[index - 1] = { ...prevSlice, endSec: removed.endSec }
      }
      setSliceCount(next.length)
      return next
    })
  }, [])

  return (
    <div className="flex flex-col gap-4">
      {/* Sample picker — sits above the waveform so switching
          samples is one click from the top of the workspace.
          The single "Upload sample" button covers both new uploads
          and re-using existing ones (the dropdown). Old local
          imports remain readable but no new ones get created
          via this UI.
          Plugin-tab embedding hides this strip entirely; the
          focused pad IS the picker in that mode. */}
      {!hideSamplePicker && (
        <section className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-surface-raised border border-brd-subtle">
          <label className="flex flex-col text-[10px] uppercase tracking-wider text-content-tertiary flex-1 min-w-[12rem]">
            Sample
            <select
              value={selectedSampleId ?? ''}
              onChange={e => setSelectedSampleId(e.target.value || undefined)}
              className="mt-0.5 rounded-md bg-surface-sunken text-content-primary text-sm px-2 py-1.5 border border-brd-subtle focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {samples.length === 0 && <option value="">No samples yet</option>}
              {samples.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {typeof s.durationSec === 'number'
                    ? ` · ${s.durationSec.toFixed(2)}s`
                    : ''}
                </option>
              ))}
            </select>
          </label>
          <SampleUploadButton
            variant="compact"
            onUploaded={sample => setSelectedSampleId(sample.id)}
          />
          {hasProjectScope && (
            <div
              role="tablist"
              aria-label="Sample filter"
              className="inline-flex items-center gap-0.5 rounded-md bg-surface-sunken p-0.5 border border-brd-subtle text-[11px] font-mono"
            >
              <button
                type="button"
                role="tab"
                aria-selected={filter === 'project'}
                onClick={() => setFilter('project')}
                className={`px-2 py-1 rounded transition-colors ${
                  filter === 'project'
                    ? 'bg-accent-muted text-accent'
                    : 'text-content-secondary hover:text-content-primary'
                }`}
                title={`Show only samples used in ${container?.name ?? 'this project'}`}
              >
                This project
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filter === 'all'}
                onClick={() => setFilter('all')}
                className={`px-2 py-1 rounded transition-colors ${
                  filter === 'all'
                    ? 'bg-accent-muted text-accent'
                    : 'text-content-secondary hover:text-content-primary'
                }`}
                title="Show every sample in your library"
              >
                All samples
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => handlePlay()}
            disabled={!buffer}
            className="btn-secondary btn-sm disabled:opacity-50"
            title="Play the full sample"
          >
            ▶ Play all
          </button>
          <button
            type="button"
            onClick={handleSendToPads}
            disabled={!selectedSample || currentSlices.length === 0}
            title={
              activeBankId
                ? `Assign the first 16 slices to Bank ${activeBankId}'s pads. Replaces whatever's there now.`
                : 'Open a project first — the slices need a bank to land on.'
            }
            className={`btn-sm disabled:opacity-50 ${
              sentToPads ? 'btn-primary' : 'btn-secondary'
            }`}
          >
            {sentToPads
              ? `✓ Sent to Bank ${activeBankId ?? ''}`
              : `→ Send to Bank ${activeBankId ?? '—'}`}
          </button>
        </section>
      )}

      <ChopControls
        bpm={bpm}
        bars={bars}
        bpmConfidence={bpmConfidence}
        snap={snap}
        sliceCount={sliceCount}
        durationSec={durationSec}
        isDetecting={isDetecting}
        isSaving={updateSample.isPending || updateLocalSample.isPending}
        chopStrategy={chopStrategy}
        peakCount={onsetPeaks.length}
        autoStartSec={autoStartSec}
        trimStartSec={trimStartSec}
        onBpmChange={setBpm}
        onBarsChange={nextBars => {
          // Bars + duration is the most authoritative BPM source —
          // exact math instead of a probabilistic detection. When
          // the user types the bar count, derive BPM from it
          // immediately so the downstream grid (16th-note snap,
          // slice boundaries) always agrees with the number they
          // just entered. Clearing bars leaves BPM alone so the
          // detector result isn't stomped on.
          setBars(nextBars)
          if (nextBars && nextBars > 0 && durationSec && durationSec > 0) {
            const exactBpm = (nextBars * 4 * 60) / durationSec
            setBpm(Math.round(exactBpm * 100) / 100)
            setBpmConfidence(1)
          }
        }}
        onSnapChange={setSnap}
        onSliceCountChange={count => chopToStrategy(count, chopStrategy)}
        onChopStrategyChange={handleStrategyChange}
        onAutoDetect={handleAutoDetect}
        onTrimToFirstTransient={handleTrimToFirstTransient}
        onSave={handleSave}
      />

      {/* Warp panel (Phase 1a). Only meaningful once both a source
          BPM (set by Auto-detect or manual entry) and a project BPM
          are known. Hidden otherwise so we don't tempt the user into
          a warp that would divide by zero. */}
      {buffer && bpm && projectBpm && (
        <section
          aria-label="Warp to project BPM"
          className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-sunken border border-brd-subtle px-3 py-2"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
            Warp
          </span>
          <span className="text-[11px] font-mono text-content-secondary tabular-nums">
            {bpm.toFixed(1)} → {projectBpm.toFixed(1)} BPM
          </span>
          <label className="flex items-center gap-1 text-[10px] font-mono text-content-tertiary">
            Mode
            <select
              value={warpMode}
              onChange={e => setWarpMode(e.target.value as WarpMode)}
              disabled={warping}
              className="rounded-md bg-surface-base border border-brd-subtle px-1.5 py-0.5 text-[10px] font-mono text-content-primary focus:outline-none focus:border-[color:var(--accent-halo)]"
              title="beats: transient-aware (drums); tones: melodic; texture: pads; complex: generic"
            >
              <option value="beats">beats</option>
              <option value="tones">tones</option>
              <option value="texture">texture</option>
              <option value="complex">complex</option>
            </select>
          </label>
          <button
            type="button"
            onClick={handleWarp}
            disabled={warping || Math.abs(bpm - projectBpm) < 0.01}
            className="btn-primary btn-sm disabled:opacity-50"
            title={
              Math.abs(bpm - projectBpm) < 0.01
                ? 'Sample is already at the project tempo'
                : `Render a pitch-preserved version at ${projectBpm.toFixed(1)} BPM`
            }
          >
            {warping ? 'Warping…' : 'Warp to project BPM'}
          </button>
          {warpedTo !== null && (
            <>
              <span
                className="ml-auto text-[10px] font-mono text-accent tabular-nums"
                title="Currently playing a warped copy of the source"
              >
                · warped @ {warpedTo.toFixed(1)}
              </span>
              <button
                type="button"
                onClick={handleResetWarp}
                disabled={warping}
                className="btn-ghost btn-sm"
                title="Swap back to the original unwarped buffer"
              >
                Reset
              </button>
            </>
          )}
        </section>
      )}

      {/* Sample gain + Normalize (F1). Gain is multiplicative with
          velocity + slice gain at playback time. Normalize scans the
          decoded buffer's peak, computes the dB that lands it at
          -3 dBFS, and writes it to `sampleGainDb` without touching
          the source bytes (non-destructive). */}
      {buffer && selectedSample && (
        <section
          aria-label="Sample gain"
          className="flex flex-wrap items-center gap-3 rounded-xl bg-surface-sunken border border-brd-subtle px-3 py-2"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
            Gain
          </span>
          <input
            type="range"
            min={-24}
            max={24}
            step={0.5}
            value={sampleGainDb ?? 0}
            onChange={e => {
              const v = Number(e.target.value)
              setSampleGainDb(v === 0 ? undefined : v)
              // Manual edit clears the "this was normalized" badge
              // so the user knows the field is no longer a normalize
              // output.
              setNormalizedPeak(undefined)
            }}
            className="flex-1 min-w-[160px] accent-[color:var(--accent)]"
            aria-label="Sample gain in decibels"
          />
          <span className="text-[11px] font-mono text-content-secondary tabular-nums w-16 text-right">
            {(sampleGainDb ?? 0) > 0 ? '+' : ''}
            {(sampleGainDb ?? 0).toFixed(1)} dB
          </span>
          <button
            type="button"
            onClick={() => {
              if (!buffer) return
              const result = computeNormalizeGain(buffer)
              if (!result) return
              setSampleGainDb(Math.round(result.gainDb * 10) / 10)
              setNormalizedPeak(result.normalizedPeak)
            }}
            title="Non-destructively scale to -3 dBFS peak. Safe to undo by resetting gain to 0 dB."
            className="px-3 py-1 rounded text-[10px] font-mono border border-brd-subtle text-content-secondary hover:border-accent hover:text-accent transition-colors"
          >
            Normalize
          </button>
          <button
            type="button"
            onClick={() => {
              setSampleGainDb(undefined)
              setNormalizedPeak(undefined)
            }}
            disabled={sampleGainDb === undefined || sampleGainDb === 0}
            title="Reset gain back to 0 dB"
            className="px-2 py-1 rounded text-[10px] font-mono border border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent transition-colors disabled:opacity-40 disabled:hover:border-brd-subtle disabled:hover:text-content-tertiary"
          >
            Reset
          </button>
          {normalizedPeak !== undefined && normalizedPeak > 0 && (
            <span
              className="text-[10px] font-mono text-accent tabular-nums"
              title={`Last normalize found a peak of ${normalizedPeak.toFixed(3)} (≈ ${linearToDb(normalizedPeak).toFixed(1)} dBFS)`}
            >
              · peak {linearToDb(normalizedPeak).toFixed(1)} dBFS
            </span>
          )}
        </section>
      )}

      {/* Per-slice gain strip (F1). Shows one row per slice with
          its own dB slider + Normalize button. Limited to slices
          whose region is non-zero length. */}
      {buffer && currentSlices.length > 0 && (
        <details className="rounded-xl bg-surface-sunken border border-brd-subtle">
          <summary className="cursor-pointer select-none px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary hover:text-content-secondary">
            ▸ Per-slice gain · {currentSlices.length}{' '}
            {currentSlices.length === 1 ? 'slice' : 'slices'}
          </summary>
          <div className="px-3 pb-2 pt-1 flex flex-col gap-1 max-h-[200px] overflow-y-auto">
            {currentSlices.map((slice, idx) => {
              const sliceGain = slice.gainDb ?? 0
              const setSlice = (patch: Partial<SampleSlice>) => {
                setCurrentSlices(prev =>
                  prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
                )
              }
              return (
                <div
                  key={slice.id}
                  className="flex items-center gap-2 text-[10px] font-mono"
                >
                  <span className="w-6 text-content-tertiary tabular-nums">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="w-24 text-content-secondary truncate">
                    {slice.label ?? `${slice.startSec.toFixed(2)}s`}
                  </span>
                  <input
                    type="range"
                    min={-24}
                    max={24}
                    step={0.5}
                    value={sliceGain}
                    onChange={e => {
                      const v = Number(e.target.value)
                      setSlice({ gainDb: v === 0 ? undefined : v })
                    }}
                    className="flex-1 accent-[color:var(--accent)]"
                    aria-label={`Slice ${idx + 1} gain in decibels`}
                  />
                  <span className="w-14 text-right tabular-nums text-content-secondary">
                    {sliceGain > 0 ? '+' : ''}
                    {sliceGain.toFixed(1)} dB
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const result = computeNormalizeGain(
                        buffer,
                        slice.startSec,
                        slice.endSec,
                      )
                      if (!result) return
                      setSlice({ gainDb: Math.round(result.gainDb * 10) / 10 })
                    }}
                    title="Normalize just this slice's region to -3 dBFS"
                    className="px-2 py-0.5 rounded border border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent transition-colors"
                  >
                    Norm
                  </button>
                  <button
                    type="button"
                    onClick={() => setSlice({ gainDb: undefined })}
                    disabled={slice.gainDb === undefined}
                    title="Reset this slice's gain"
                    className="px-1.5 py-0.5 rounded border border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent transition-colors disabled:opacity-40"
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
        </details>
      )}

      {/* Zoom controls — discrete steps so the user always lands on
          a sensible level. 1× fits the whole sample to the
          container; higher levels scale the canvas + markers and
          let the parent section scroll horizontally for precision
          editing. */}
      <div className="flex items-center justify-between gap-2 -mb-2 px-1">
        <div className="text-[10px] uppercase tracking-wider text-content-tertiary">
          Waveform
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-content-tertiary tabular-nums w-10 text-right">
            {zoomLevel}×
          </span>
          <button
            type="button"
            onClick={() =>
              setZoomLevel(z => Math.max(1, Number((z / 2).toFixed(2))))
            }
            disabled={zoomLevel <= 1}
            className="px-2 py-0.5 rounded text-[11px] font-mono border border-brd-subtle bg-surface-sunken text-content-secondary hover:text-content-primary disabled:opacity-40"
            title="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            onClick={() => setZoomLevel(z => Math.min(64, z * 2))}
            disabled={zoomLevel >= 64}
            className="px-2 py-0.5 rounded text-[11px] font-mono border border-brd-subtle bg-surface-sunken text-content-secondary hover:text-content-primary disabled:opacity-40"
            title="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => setZoomLevel(1)}
            disabled={zoomLevel === 1}
            className="px-2 py-0.5 rounded text-[11px] font-mono border border-brd-subtle bg-surface-sunken text-content-secondary hover:text-content-primary disabled:opacity-40"
            title="Fit waveform to container"
          >
            Fit
          </button>
        </div>
      </div>

      <section
        ref={containerRef}
        className="relative w-full rounded-lg border border-brd-subtle bg-surface-raised overflow-x-auto overflow-y-hidden"
      >
        {/* Inner wrapper sized to the zoomed width — scrolls inside
            the parent section when zoomLevel > 1. All overlays
            (waveform canvas, slice markers, trim markers, the slice
            index ribbon below) share this width so every horizontal
            position stays aligned. */}
        <div
          className="relative"
          style={{ width: width * zoomLevel, height: height + 18 }}
        >
          <div
            style={{ position: 'relative', width: width * zoomLevel, height }}
          >
            <WaveformViewer
              buffer={buffer}
              width={width * zoomLevel}
              height={height}
              bpm={bpm}
              bars={bars}
              showGrid
              trimStartSec={trimStartSec}
              trimEndSec={trimEndSec}
            />
            {buffer && durationSec && durationSec > 0 && (
              <>
                <SliceMarkers
                  boundaries={boundaries}
                  durationSec={durationSec}
                  width={width * zoomLevel}
                  height={height}
                  snapSec={snapSec}
                  onBoundariesChange={handleBoundariesChange}
                />
                <TrimMarker
                  position="start"
                  trimSec={trimStartSec}
                  durationSec={durationSec}
                  neighbourSec={Math.min(
                    currentSlices[0]?.endSec ?? durationSec,
                    trimEndSec || durationSec,
                  )}
                  width={width * zoomLevel}
                  height={height}
                  snapSec={snapSec}
                  onChange={setTrimStartSec}
                />
                <TrimMarker
                  position="end"
                  trimSec={trimEndSec || durationSec}
                  durationSec={durationSec}
                  neighbourSec={Math.max(
                    currentSlices[currentSlices.length - 1]?.startSec ?? 0,
                    trimStartSec,
                  )}
                  width={width * zoomLevel}
                  height={height}
                  snapSec={snapSec}
                  onChange={setTrimEndSec}
                />
              </>
            )}
          </div>
          {/* Slice index ribbon sits BELOW the waveform (never
              above) so touch fingers reaching for zoom / scrub
              don't obscure the sample visualisation. Scrolls in
              sync with the waveform since it lives inside the
              same zoomed wrapper. */}
          {buffer && durationSec && durationSec > 0 && (
            <SliceIndexRibbon
              slices={currentSlices}
              durationSec={durationSec}
              width={width * zoomLevel}
            />
          )}
        </div>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-content-secondary bg-surface-raised/80">
            Loading sample…
          </div>
        )}
        {loadError && !loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-rose-300 bg-surface-raised/80">
            Couldn't load sample: {loadError}
          </div>
        )}
      </section>

      {/* ScrubPad sits directly below the waveform, same visual
          width as the waveform container. X axis on the pad is
          the sample position, matching the X axis of the waveform
          above — scrubbing here reads as an extension of the
          waveform surface. */}
      <ScrubPad
        buffer={buffer}
        bpm={projectBpm ?? bpm}
        slices={currentSlices}
      />

      {/* Main-column + side-column split. Main (wide) holds the
          slice pads — the primary performance surface. Side
          (narrow) holds the always-visible GrainPlayer controls
          and the Lo-Fi presets panel; both are flat sections
          with no collapse, so everything the user might reach
          for during a chop session is one glance away. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(260px,340px)] gap-4">
        <div className="flex flex-col gap-3 min-w-0">
          {currentSlices.length > 0 && (
            <SlicePadGrid
              slices={currentSlices}
              disabled={!buffer}
              onTrigger={i => handlePlay(i)}
              onDelete={i => handleDeleteSlice(i)}
              onAssign={handleAssignSliceToPad}
            />
          )}
        </div>

        <div className="flex flex-col gap-3 min-w-0">
          <GranularPreview
            buffer={buffer}
            durationSec={durationSec}
            slices={currentSlices}
          />
          <LoFiPanel
            buffer={buffer}
            originalBuffer={originalBufferRef.current}
            onApply={next => setBuffer(next)}
            onReset={handleResetWarp}
          />
        </div>
      </div>
    </div>
  )
}
